import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NavBar,
  Button,
  SearchBar,
  Popup,
  Form,
  Input,
  Toast,
  Space,
  SpinLoading,
  Empty,
  Card,
  ImageViewer,
} from 'antd-mobile';
import { CameraOutline, DeleteOutline, AddOutline, LeftOutline, EnvironmentOutline, EyeOutline } from 'antd-mobile-icons';
import { photoFixationApi } from '../api';
import dayjs from 'dayjs';

function toRuLayout(text) {
  const map = {
    q: 'й', w: 'ц', e: 'у', r: 'к', t: 'е', y: 'н', u: 'г', i: 'ш', o: 'щ', p: 'з',
    '[': 'х', ']': 'ъ', a: 'ф', s: 'ы', d: 'в', f: 'а', g: 'п', h: 'р', j: 'о',
    k: 'л', l: 'д', ';': 'ж', "'": 'э', '\\': '\\', z: 'я', x: 'ч', c: 'с', v: 'м',
    b: 'и', n: 'т', m: 'ь', ',': 'б', '.': 'ю', '/': '.',
    Q: 'Й', W: 'Ц', E: 'У', R: 'К', T: 'Е', Y: 'Н', U: 'Г', I: 'Ш', O: 'Щ', P: 'З',
    '{': 'Х', '}': 'Ъ', A: 'Ф', S: 'Ы', D: 'В', F: 'А', G: 'П', H: 'Р', J: 'О',
    K: 'Л', L: 'Д', ':': 'Ж', '"': 'Э', '|': '/', Z: 'Я', X: 'Ч', C: 'С', V: 'М',
    B: 'И', N: 'Т', M: 'Ь', '<': 'Б', '>': 'Ю', '?': ',',
  };
  return text.split('').map((c) => map[c] || c).join('');
}

const INITIAL_LIMIT = 50;

function PhotoFixationPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedObject, setSelectedObject] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [addVisible, setAddVisible] = useState(false);
  const [addForm] = Form.useForm();

  const [historyMode, setHistoryMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryObject, setExpandedHistoryObject] = useState(null);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState([]);

  const loadObjects = async (q) => {
    setLoading(true);
    try {
      const searchQ = q ? `${q} ${toRuLayout(q)}`.trim() : '';
      const res = await photoFixationApi.searchObjects(searchQ, 200, !searchQ);
      setObjects(res.data || []);
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка загрузки объектов' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadObjects('');
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadObjects(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const requestGeo = () => {
    if (!navigator.geolocation) {
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
        setGpsLoading(false);
      },
      (err) => {
        console.warn('Geo error', err);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const loadExistingPhotos = async (objectId) => {
    setExistingLoading(true);
    try {
      const res = await photoFixationApi.getPhotos(objectId);
      setExistingPhotos(res.data || []);
    } catch (err) {
      console.warn('Ошибка загрузки отправленных фото', err);
      setExistingPhotos([]);
    } finally {
      setExistingLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await photoFixationApi.getMyPhotos();
      setHistory(res.data || []);
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка загрузки истории' });
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelect = (obj) => {
    setSelectedObject(obj);
    setPhotos([]);
    setExistingPhotos([]);
    setGps(null);
    requestGeo();
    loadExistingPhotos(obj.id);
    Toast.show({ icon: 'success', content: 'Объект выбран. Определяем геолокацию…' });
  };

  const handleChangeObject = () => {
    setSelectedObject(null);
    setPhotos([]);
    setExistingPhotos([]);
    setGps(null);
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...previews]);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].url);
      next.splice(idx, 1);
      return next;
    });
  };

  const handleUpload = async () => {
    if (!selectedObject) {
      return;
    }
    if (!photos.length) {
      Toast.show({ icon: 'fail', content: 'Добавьте фото' });
      return;
    }
    setUploading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await photoFixationApi.uploadPhotos(
        selectedObject.id,
        today,
        photos.map((p) => p.file),
        gps || undefined
      );
      Toast.show({ icon: 'success', content: 'Фото сохранены и отправлены' });
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      await loadExistingPhotos(selectedObject.id);
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка загрузки фото' });
    } finally {
      setUploading(false);
    }
  };

  const handleAddObject = async (values) => {
    try {
      await photoFixationApi.createObject({
        name: values.name,
        address: values.address,
        case_number: values.case_number,
      });
      // Обновляем список: новый объект появится сверху, фото можно добавить позже
      await loadObjects('');
      setAddVisible(false);
      addForm.resetFields();
      Toast.show({ icon: 'success', content: 'Объект добавлен. Выберите его, чтобы прикрепить фото.' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка добавления объекта' });
    }
  };

  const groupedExistingPhotos = useMemo(() => {
    const groups = {};
    existingPhotos.forEach((p) => {
      const d = p.shooting_date || 'Без даты';
      if (!groups[d]) groups[d] = [];
      groups[d].push(p);
    });
    return Object.entries(groups).sort((a, b) => (a[0] > b[0] ? -1 : 1));
  }, [existingPhotos]);

  const groupPhotosByDate = (photos) => {
    const groups = {};
    photos.forEach((p) => {
      const d = p.shooting_date || 'Без даты';
      if (!groups[d]) groups[d] = [];
      groups[d].push(p);
    });
    return Object.entries(groups).sort((a, b) => (a[0] > b[0] ? -1 : 1));
  };

  const openPreview = (images, startIndex) => {
    setPreviewImages(images);
    setPreviewIndex(startIndex);
    setPreviewVisible(true);
  };

  // Экран истории фотофиксации
  if (historyMode) {
    return (
      <div style={{ padding: '0 12px 20px' }}>
        <NavBar
          onBack={() => setHistoryMode(false)}
          style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
        >
          История фотофиксации
        </NavBar>

        <div style={{ padding: '12px 0' }}>
          <Button
            block
            color="primary"
            fill="outline"
            onClick={loadHistory}
            style={{ borderRadius: 12 }}
          >
            Обновить историю
          </Button>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 160px)' }}>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <SpinLoading />
            </div>
          ) : history.length === 0 ? (
            <Empty
              description="Вы ещё не делали фотофиксацию"
              style={{ paddingTop: 40 }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {history.map(({ object: obj, photos: objPhotos }) => {
                const isExpanded = expandedHistoryObject === obj.id;
                const photoCount = objPhotos.length;
                const firstPhoto = objPhotos[0];
                return (
                  <div
                    key={obj.id}
                    style={{
                      padding: 14,
                      borderRadius: 12,
                      background: '#fff',
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ fontSize: 15, color: '#262626', fontWeight: 500, lineHeight: 1.3 }}>
                      {obj.name}
                    </div>
                    {obj.address && (
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                        {obj.address}
                      </div>
                    )}
                    {obj.case_number && (
                      <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4 }}>
                        № дела: {obj.case_number}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: '#595959', marginTop: 8 }}>
                      Фото: {photoCount}
                    </div>

                    {!isExpanded && (
                      <Space wrap style={{ marginTop: 10 }}>
                        {objPhotos.slice(0, 3).map((p, idx) => (
                          <div
                            key={p.id}
                            onClick={() => openPreview(objPhotos.map((ph) => ph.s3_url), idx)}
                            style={{ position: 'relative', width: 64, height: 64 }}
                          >
                            <img
                              src={p.s3_url}
                              alt={p.filename}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                            />
                          </div>
                        ))}
                        {photoCount > 3 && (
                          <div
                            onClick={() => setExpandedHistoryObject(obj.id)}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 8,
                              background: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              color: '#595959',
                            }}
                          >
                            +{photoCount - 3}
                          </div>
                        )}
                      </Space>
                    )}

                    {isExpanded && (
                      <div style={{ marginTop: 12 }}>
                        {groupPhotosByDate(objPhotos).map(([dateStr, items]) => {
                          const images = items.map((p) => p.s3_url);
                          return (
                            <div key={dateStr} style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                                {dateStr !== 'Без даты'
                                  ? dayjs(dateStr).format('DD.MM.YYYY')
                                  : dateStr}
                              </div>
                              <Space wrap>
                                {items.map((p, idx) => (
                                  <div
                                    key={p.id}
                                    onClick={() => openPreview(images, idx)}
                                    style={{ position: 'relative', width: 80, height: 80 }}
                                  >
                                    <img
                                      src={p.s3_url}
                                      alt={p.filename}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                    />
                                    <div
                                      style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 22,
                                        background: 'rgba(0,0,0,0.4)',
                                        borderRadius: '0 0 8px 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <EyeOutline style={{ color: '#fff', fontSize: 12 }} />
                                    </div>
                                  </div>
                                ))}
                              </Space>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Button
                      size="small"
                      color="primary"
                      fill="outline"
                      onClick={() => setExpandedHistoryObject(isExpanded ? null : obj.id)}
                      style={{ marginTop: 10, borderRadius: 8 }}
                    >
                      {isExpanded ? 'Свернуть' : 'Показать все фото'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <ImageViewer.Multi
          images={previewImages}
          visible={previewVisible}
          defaultIndex={previewIndex}
          onClose={() => setPreviewVisible(false)}
        />
      </div>
    );
  }

  // Экран выбора объекта
  if (!selectedObject) {
    return (
      <div style={{ padding: '0 12px 20px' }}>
        <NavBar
          onBack={() => navigate(-1)}
          style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
        >
          Выберите объект
        </NavBar>

        <div style={{ padding: '12px 0' }}>
          <Button
            block
            color="primary"
            fill="outline"
            onClick={() => {
              setHistoryMode(true);
              loadHistory();
            }}
            style={{ borderRadius: 12, marginBottom: 12 }}
          >
            <EyeOutline style={{ marginRight: 6 }} />
            Моя история фотофиксации
          </Button>
          <SearchBar
            placeholder="Поиск по названию, адресу или № дела"
            value={query}
            onChange={(v) => setQuery(v)}
          />
        </div>

        <Button
          block
          color="primary"
          fill="outline"
          onClick={() => setAddVisible(true)}
          style={{ borderRadius: 12, marginBottom: 12 }}
        >
          <AddOutline style={{ marginRight: 4 }} />
          Добавить объект вручную
        </Button>

        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <SpinLoading />
            </div>
          ) : objects.length === 0 ? (
            <Empty
              description={query.trim() ? 'Ничего не найдено' : 'Нет объектов'}
              style={{ paddingTop: 40 }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {objects.map((obj) => (
                <div
                  key={obj.id}
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ fontSize: 15, color: '#262626', fontWeight: 500, lineHeight: 1.3 }}>
                    {obj.name}
                  </div>
                  {obj.address && (
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                      {obj.address}
                    </div>
                  )}
                  {obj.case_number && (
                    <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4 }}>
                      № дела: {obj.case_number}
                    </div>
                  )}
                  <Button
                    size="small"
                    color="primary"
                    fill="outline"
                    onClick={() => handleSelect(obj)}
                    style={{ marginTop: 10, borderRadius: 8 }}
                  >
                    Выбрать объект
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Popup
          visible={addVisible}
          onMaskClick={() => setAddVisible(false)}
          position="bottom"
          bodyStyle={{ height: '65vh', borderRadius: '16px 16px 0 0' }}
        >
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Новый объект</div>
            <Form layout="vertical" form={addForm} onFinish={handleAddObject}>
              <Form.Item
                name="name"
                label="Наименование объекта"
                rules={[{ required: true, message: 'Укажите наименование' }]}
              >
                <Input placeholder="Например, Жилой дом" />
              </Form.Item>
              <Form.Item name="address" label="Адрес">
                <Input placeholder="г. Москва, ..." />
              </Form.Item>
              <Form.Item name="case_number" label="№ дела">
                <Input placeholder="12345" />
              </Form.Item>
              <Button block type="submit" color="primary" style={{ borderRadius: 12, height: 44 }}>
                Добавить
              </Button>
            </Form>
          </div>
        </Popup>
      </div>
    );
  }

  // Экран фотофиксации
  return (
    <div style={{ padding: '0 12px 100px' }}>
      <NavBar
        onBack={() => navigate(-1)}
        style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
      >
        Фотофиксация
      </NavBar>

      {/* Карточка объекта */}
      <Card style={{ marginTop: 16, borderRadius: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: 1.4 }}>
          {selectedObject.name}
        </div>
        {selectedObject.address && (
          <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 6 }}>
            {selectedObject.address}
          </div>
        )}
        {selectedObject.case_number && (
          <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 4 }}>
            № дела: {selectedObject.case_number}
          </div>
        )}
        {selectedObject.is_manual && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#1677ff' }}>
            Объект добавлен вручную
          </div>
        )}
        {gps && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a', display: 'flex', alignItems: 'center', gap: 4 }}>
            <EnvironmentOutline />
            Геолокация получена
          </div>
        )}
        {gpsLoading && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Определение геолокации…
          </div>
        )}
        <Button
          size="small"
          color="primary"
          fill="outline"
          onClick={handleChangeObject}
          style={{ marginTop: 12, borderRadius: 8 }}
        >
          <LeftOutline style={{ marginRight: 4 }} />
          Изменить объект
        </Button>
      </Card>

      {/* Отправленные фото */}
      <div style={{ marginTop: 20 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Отправленные фото
          {existingPhotos.length > 0 && (
            <span style={{ color: '#8c8c8c', fontWeight: 400, marginLeft: 6 }}>
              ({existingPhotos.length})
            </span>
          )}
        </div>
        {existingLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <SpinLoading style={{ '--size': '24px' }} />
          </div>
        ) : existingPhotos.length === 0 ? (
          <div style={{ fontSize: 13, color: '#8c8c8c', padding: '8px 0' }}>
            Пока нет отправленных фото
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {groupedExistingPhotos.map(([dateStr, items]) => {
              const images = items.map((p) => p.s3_url);
              return (
                <div key={dateStr}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                    {dateStr !== 'Без даты'
                      ? dayjs(dateStr).format('DD.MM.YYYY')
                      : dateStr}
                  </div>
                  <Space wrap>
                    {items.map((p, idx) => (
                      <div
                        key={p.id}
                        onClick={() => openPreview(images, idx)}
                        style={{ position: 'relative', width: 88, height: 88 }}
                      >
                        <img
                          src={p.s3_url}
                          alt={p.filename}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 24,
                            background: 'rgba(0,0,0,0.4)',
                            borderRadius: '0 0 10px 10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <EyeOutline style={{ color: '#fff', fontSize: 14 }} />
                        </div>
                      </div>
                    ))}
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Фото */}
      <div style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Новые фото
        </div>
        <input
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFilesChange}
        />
        <Button
          block
          color="primary"
          fill="outline"
          onClick={() => fileInputRef.current?.click()}
          style={{ borderRadius: 12 }}
        >
          <CameraOutline style={{ marginRight: 6 }} />
          Сделать фото
        </Button>
      </div>

      {/* Предпросмотр */}
      {photos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>
            Выбрано фото: {photos.length} (нажмите для просмотра)
          </div>
          <Space wrap>
            {photos.map((p, idx) => (
              <div key={idx} style={{ position: 'relative', width: 88, height: 88 }}>
                <img
                  src={p.url}
                  alt="preview"
                  onClick={() => openPreview(photos.map((ph) => ph.url), idx)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                />
                <div
                  onClick={() => removePhoto(idx)}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#ff4d4f',
                    color: '#fff',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  <DeleteOutline style={{ fontSize: 12 }} />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 24,
                    background: 'rgba(0,0,0,0.4)',
                    borderRadius: '0 0 10px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EyeOutline style={{ color: '#fff', fontSize: 14 }} />
                </div>
              </div>
            ))}
          </Space>
        </div>
      )}

      {/* Кнопка сохранить */}
      <div style={{ marginTop: 28 }}>
        <Button
          block
          color="primary"
          loading={uploading}
          onClick={handleUpload}
          style={{ borderRadius: 12, height: 46 }}
        >
          Сохранить и отправить
        </Button>
      </div>

      {/* Просмотрщик фото */}
      <ImageViewer.Multi
        images={previewImages}
        visible={previewVisible}
        defaultIndex={previewIndex}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
}

export default PhotoFixationPage;
